import { useState, useRef, useEffect, useLayoutEffect, type ReactNode } from 'react'

interface AccordionItem {
  id: string | number
  header: ReactNode
  content: ReactNode
  disabled?: boolean
}

interface AccordionProps {
  items: AccordionItem[]
  activeId?: string | number | null
  onToggle?: (id: string | number | null) => void
  multiple?: boolean
  /** `surface` (default) uses mc-section; `glass` wraps each panel in liquid-glass; `bare` has no box (navigation groups: only the header row and the panel). */
  variant?: 'surface' | 'glass' | 'flat' | 'bare'
  /** Mehrere gleichzeitig offene Felder von außen steuern (z. B. „Alle aufklappen"); dann meldet `onToggleId` jeden Klick. */
  openIds?: Array<string | number>
  onToggleId?: (id: string | number) => void
  /** Inhalt erst beim ersten Aufklappen einhängen (danach bleibt er, damit Zustand erhalten bleibt) — für schwere Inhalte
   *  wie Videos. Standard: sofort einhängen (bisheriges Verhalten). */
  lazy?: boolean
  /** Optional classes of a page that styles its own rows (the Schulportal led-school rows): `className` replaces the spacing of the list, `itemClassName` is added to each panel's outer box, `headerClassName` to each header button. */
  className?: string
  itemClassName?: string
  headerClassName?: string
  /** Where the chevron sits: `end` (default) at the right, rotating down; `start` in front of the header (a list of rows, e.g. the Schulportal Spüler list), turned right while shut. */
  chevron?: 'start' | 'end'
}

export function Accordion({ items, activeId, onToggle, multiple = false, variant = 'surface', lazy = false, openIds, onToggleId, className, itemClassName, headerClassName, chevron = 'end' }: AccordionProps) {
  const [internalOpen, setInternalOpen] = useState<Set<string | number>>(
    activeId != null ? new Set([activeId]) : new Set()
  )

  const isControlled = onToggle !== undefined
  const openSet = openIds ? new Set(openIds) : isControlled && activeId != null ? new Set([activeId]) : internalOpen

  function toggle(id: string | number) {
    if (onToggleId) {
      onToggleId(id)
    } else if (isControlled) {
      onToggle!(openSet.has(id) ? null : id)
    } else {
      setInternalOpen(prev => {
        const next = new Set(multiple ? prev : [])
        if (prev.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    }
  }

  return (
    <div className={className ?? (variant === 'bare' ? 'space-y-0.5' : 'space-y-2')}>
      {items.map(item => (
        <AccordionPanel
          key={item.id}
          header={item.header}
          open={openSet.has(item.id)}
          disabled={item.disabled}
          onToggle={() => !item.disabled && toggle(item.id)}
          variant={variant}
          lazy={lazy}
          itemClassName={itemClassName}
          headerClassName={headerClassName}
          chevron={chevron}
        >
          {item.content}
        </AccordionPanel>
      ))}
    </div>
  )
}

interface PanelProps {
  header: ReactNode
  open: boolean
  disabled?: boolean
  onToggle: () => void
  children: ReactNode
  variant?: 'surface' | 'glass' | 'flat' | 'bare'
  lazy?: boolean
  itemClassName?: string
  headerClassName?: string
  chevron?: 'start' | 'end'
}

// Reduced motion OR the Schulportal "Bewegung" switch (data-bewegung="aus" on the root, school/lib/bewegung.ts) stops the motion.
const reduzierteBewegung = () => typeof window !== 'undefined' && (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  || document.documentElement.getAttribute('data-bewegung') === 'aus')

function AccordionPanel({ header, open, disabled, onToggle, children, variant = 'surface', lazy = false, itemClassName, headerClassName, chevron = 'end' }: PanelProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number>(0)
  // lazy: einmal aufgeklappt, bleibt der Inhalt eingehängt (Zustand, laufende Videos).
  const [eingehaengt, setEingehaengt] = useState(!lazy || open)
  useEffect(() => { if (open) setEingehaengt(true) }, [open])

  // A panel that is open from its first render (the current group of the navigation, "Mehr" opening) shows open at once: the height is measured before the
  // first paint and the transitions switch on only after it. Later toggles animate as before.
  const [animiert, setAnimiert] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimiert(true))
    return () => cancelAnimationFrame(id)
  }, [])
  useLayoutEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight)
      // Zugeklappter Inhalt ist weder per Tab noch per Screenreader erreichbar.
      if (open) contentRef.current.removeAttribute('inert')
      else contentRef.current.setAttribute('inert', '')
    }
  }, [open, children, eingehaengt])

  // Die Höhe folgt dem Inhalt, solange das Feld offen ist — sonst schneidet max-height nachwachsende Inhalte ab
  // (Fehlermeldung, Player, laufender Auftrag).
  useEffect(() => {
    const el = contentRef.current
    if (!open || !el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setHeight(el.scrollHeight))
    ro.observe(el)
    return () => ro.disconnect()
  }, [open, eingehaengt])

  const ohneBewegung = reduzierteBewegung() || !animiert
  const pfeil = (
    <span
      className="material-symbols-outlined !text-[18px] text-on-surface-variant flex-shrink-0"
      style={{
        transition: ohneBewegung ? 'none' : 'transform 400ms cubic-bezier(0.2,0.8,0.2,1)',
        transform: chevron === 'start' ? (open ? 'rotate(0deg)' : 'rotate(-90deg)') : (open ? 'rotate(180deg)' : 'rotate(0deg)'),
      }}
    >
      expand_more
    </span>
  )

  return (
    <div className={`overflow-hidden ${variant === 'glass' ? 'liquid-glass rounded-2xl p-4' : variant === 'flat' ? 'rounded-xl border border-outline-variant/40 p-3' : variant === 'bare' ? '' : 'mc-section'}${itemClassName ? ` ${itemClassName}` : ''}`}>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={`w-full flex items-center gap-3 text-left select-none disabled:opacity-50 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-primary ${variant === 'bare' ? 'min-h-12 px-4 rounded-xl hover:bg-surface-container-low' : ''}${headerClassName ? ` ${headerClassName}` : ''}`}
        aria-expanded={open}
      >
        {chevron === 'start' && pfeil}
        <div className="flex-1 min-w-0">{header}</div>
        {chevron === 'end' && pfeil}
      </button>
      <div
        style={{
          maxHeight: open ? height : 0,
          opacity: open ? 1 : 0,
          transition: ohneBewegung ? 'none' : 'max-height 400ms cubic-bezier(0.2,0.8,0.2,1), opacity 300ms ease',
          overflow: 'hidden',
        }}
      >
        <div ref={contentRef} className={variant === 'flat' ? 'pt-3' : variant === 'bare' ? 'pt-0.5' : 'pt-4'} aria-hidden={!open}>
          {eingehaengt ? children : null}
        </div>
      </div>
    </div>
  )
}
